// alertService.js - API client for Alert Configuration feature
// Handles all alert-related API calls with authentication

const API_BASE_URL = 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

/**
 * Helper to get auth token (idToken for CIAM)
 */
async function getAuthToken() {
    try {
        const msalInstance = window.msalInstance;
        if (!msalInstance) return null;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return null;

        const request = {
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0],
        };

        const response = await msalInstance.acquireTokenSilent(request);
        return response.idToken;
    } catch (error) {
        console.log('Failed to acquire token:', error.message);
        return null;
    }
}

/**
 * Helper to build headers with auth token
 */
async function getHeaders() {
    const token = await getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
}

/**
 * Get alert configuration for the authenticated user
 * @returns {Promise<object>} - { success, config }
 */
export async function getAlertConfig() {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/alert-config-get`, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get alert config (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting alert config:', error);
        throw error;
    }
}

/**
 * Update alert configuration
 * @param {object} config - Configuration to update
 * @returns {Promise<object>} - { success, message, config }
 */
export async function updateAlertConfig(config) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/alert-config-update`, {
            method: 'POST',
            headers,
            body: JSON.stringify(config),
        });

        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
            const error = new Error(responseData.error || `Failed to update alert config (${response.status})`);
            error.statusCode = response.status;
            if (responseData.validationErrors) {
                error.validationErrors = responseData.validationErrors;
            }
            throw error;
        }

        return responseData;
    } catch (error) {
        console.error('Error updating alert config:', error);
        throw error;
    }
}

/**
 * Enable scan completed alerts
 * @param {object} settings - { emails, webhook }
 * @returns {Promise<object>}
 */
export async function enableScanCompletedAlerts(settings = {}) {
    return updateAlertConfig({
        scanCompletedEnabled: true,
        scanCompletedEmails: settings.emails || '',
        scanCompletedWebhook: settings.webhook || null,
    });
}

/**
 * Disable scan completed alerts
 * @returns {Promise<object>}
 */
export async function disableScanCompletedAlerts() {
    return updateAlertConfig({
        scanCompletedEnabled: false,
    });
}

/**
 * Enable new findings alerts
 * @param {object} settings - { minSeverity, emails, webhook }
 * @returns {Promise<object>}
 */
export async function enableNewFindingsAlerts(settings = {}) {
    return updateAlertConfig({
        newFindingsEnabled: true,
        newFindingsMinSeverity: settings.minSeverity || 'high',
        newFindingsEmails: settings.emails || '',
        newFindingsWebhook: settings.webhook || null,
    });
}

/**
 * Disable new findings alerts
 * @returns {Promise<object>}
 */
export async function disableNewFindingsAlerts() {
    return updateAlertConfig({
        newFindingsEnabled: false,
    });
}

/**
 * Enable new ports alerts
 * @param {object} settings - { emails, webhook }
 * @returns {Promise<object>}
 */
export async function enableNewPortsAlerts(settings = {}) {
    return updateAlertConfig({
        newPortsEnabled: true,
        newPortsEmails: settings.emails || '',
        newPortsWebhook: settings.webhook || null,
    });
}

/**
 * Disable new ports alerts
 * @returns {Promise<object>}
 */
export async function disableNewPortsAlerts() {
    return updateAlertConfig({
        newPortsEnabled: false,
    });
}

/**
 * Set delivery mode
 * @param {string} mode - 'immediate', 'daily', or 'weekly'
 * @param {object} settings - { digestTime, digestDay, timezone }
 * @returns {Promise<object>}
 */
export async function setDeliveryMode(mode, settings = {}) {
    const config = { deliveryMode: mode };

    if (mode === 'daily' || mode === 'weekly') {
        if (settings.digestTime) config.digestTime = settings.digestTime;
        if (settings.timezone) config.timezone = settings.timezone;
    }

    if (mode === 'weekly' && settings.digestDay !== undefined) {
        config.digestDay = settings.digestDay;
    }

    return updateAlertConfig(config);
}

// Severity options for UI
export const SEVERITY_OPTIONS = [
    { value: 'critical', label: 'Critical only' },
    { value: 'high', label: 'High and above' },
    { value: 'medium', label: 'Medium and above' },
    { value: 'low', label: 'Low and above' },
];

// Delivery mode options for UI
export const DELIVERY_MODE_OPTIONS = [
    { value: 'immediate', label: 'Immediate', description: 'Send alerts as they occur' },
    { value: 'daily', label: 'Daily digest', description: 'Send a summary once per day' },
    { value: 'weekly', label: 'Weekly digest', description: 'Send a summary once per week' },
];

// Day of week options for weekly digest
export const DAY_OPTIONS = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
];

// Default export for convenience
const alertService = {
    getAlertConfig,
    updateAlertConfig,
    enableScanCompletedAlerts,
    disableScanCompletedAlerts,
    enableNewFindingsAlerts,
    disableNewFindingsAlerts,
    enableNewPortsAlerts,
    disableNewPortsAlerts,
    setDeliveryMode,
    SEVERITY_OPTIONS,
    DELIVERY_MODE_OPTIONS,
    DAY_OPTIONS,
};

export default alertService;
